import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { LayoutDashboard, Building2, FileText, Wrench, Menu, X, LogOut, User, Bell, AlertTriangle } from 'lucide-react';
import TopBar from './TopBar';
import { useUser, useLogout, useResendVerification } from '../hooks/useAuth';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const Layout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [resentStatus, setResentStatus] = useState(null); // 'sent' | 'already-verified' | null
  const { data: user } = useUser();
  const logout = useLogout();
  const resendVerification = useResendVerification();
  const { t } = useLanguage();

  const handleResend = async () => {
    setResentStatus(null);
    try {
      const data = await resendVerification.mutateAsync();
      setResentStatus(data?.status === 'already-verified' ? 'already-verified' : 'sent');
    } catch {
      // keep button available for retry
    }
  };

  const showVerificationBanner = !bannerDismissed && user && !user.email_verified_at;

  const navSections = [
    {
      label: t(KEYS.NAV_SECTION_MAIN),
      items: [
        { path: '/', label: t(KEYS.NAV_DASHBOARD), icon: LayoutDashboard },
      ],
    },
    {
      label: t(KEYS.NAV_SECTION_MANAGEMENT),
      items: [
        { path: '/suppliers', label: t(KEYS.NAV_SUPPLIERS), icon: Building2 },
        { path: '/services', label: t(KEYS.NAV_SERVICES), icon: Wrench },
        { path: '/invoices', label: t(KEYS.NAV_INVOICES), icon: FileText },
        { path: '/reminders', label: t(KEYS.NAV_REMINDERS), icon: Bell },
      ],
    },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout.mutate();
  };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div className="flex flex-col min-h-screen bg-surface-base">
      <TopBar />
      
      <div className="flex flex-1 pt-14">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile menu button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="fixed bottom-5 right-5 z-50 lg:hidden p-3.5 bg-accent text-surface-base rounded-full shadow-glow-md hover:bg-accent-hover transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed top-14 bottom-0 left-0 z-40 w-56 
          bg-surface-raised 
          border-r border-surface-border 
          transform transition-transform duration-200 ease-out
          flex flex-col
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-5">
            {navSections.map((section) => (
              <div key={section.label}>
                <p className="px-3 mb-2 text-[10px] font-medium text-text-muted uppercase tracking-widest">
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
                          transition-colors duration-150
                          ${active 
                            ? 'bg-surface-overlay text-accent' 
                            : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary'
                          }
                        `}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar bottom — user section */}
          <div className="px-3 py-4 border-t border-surface-border">
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-8 h-8 rounded-full border border-accent/40 bg-surface-overlay flex items-center justify-center text-xs font-semibold text-accent">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  {user?.name || user?.email || 'User'}
                </p>
              </div>
            </div>
            <div className="space-y-0.5">
              <Link
                to="/notifications"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive('/notifications')
                    ? 'bg-surface-overlay text-accent'
                    : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary'
                }`}
              >
                <Bell className="w-4 h-4" />
                <span>{t(KEYS.NAV_NOTIFICATIONS)}</span>
              </Link>
              <Link
                to="/profile"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive('/profile') 
                    ? 'bg-surface-overlay text-accent' 
                    : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary'
                }`}
              >
                <User className="w-4 h-4" />
                <span>{t(KEYS.NAV_PROFILE)}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-text-secondary hover:bg-surface-overlay hover:text-status-overdue transition-colors duration-150 w-full cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                <span>{t(KEYS.NAV_LOGOUT)}</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 lg:ml-56 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto animate-appear">
            {/* Email verification banner */}
            {showVerificationBanner && (
              <div className="mb-6 flex items-start gap-3 p-3 rounded-md bg-status-pending/10 border border-status-pending/20 text-status-pending animate-appear">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                  <span>{t(KEYS.LAYOUT_VERIFY_EMAIL)}</span>
                  {resentStatus === 'sent' && (
                    <span className="ml-2 text-status-paid">{t(KEYS.LAYOUT_VERIFY_EMAIL_SENT)}</span>
                  )}
                  {resentStatus === 'already-verified' && (
                    <span className="ml-2 text-text-muted">{t(KEYS.LAYOUT_VERIFY_EMAIL_ALREADY)}</span>
                  )}
                  {!resentStatus && (
                    <button
                      onClick={handleResend}
                      disabled={resendVerification.isPending}
                      className="ml-2 underline hover:no-underline disabled:opacity-50 cursor-pointer"
                    >
                      {resendVerification.isPending ? t(KEYS.LAYOUT_VERIFY_EMAIL_RESENDING) : t(KEYS.LAYOUT_VERIFY_EMAIL_RESEND)}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setBannerDismissed(true)}
                  className="shrink-0 text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
